import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './providers/AuthProvider';
import type { UserRole } from '@/shared/types/domain';
import { Spinner } from '@/shared/components/Spinner';

interface ProtectedRouteProps {
  children: ReactNode;
  /** If omitted, any authenticated user may access the route. */
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  // `user === undefined` means the initial silent-refresh attempt (see
  // AuthProvider) hasn't resolved yet -- show a loading state rather than
  // flashing the login page for a user whose session is actually still valid.
  if (user === undefined) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-7)' }}>
        <Spinner label="Checking your session" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    const fallback = user.role === 'SystemAdmin' || user.role === 'EventOrganiser' ? '/admin/events' : '/dashboard';
    return <Navigate to={fallback} replace />;
}

  return <>{children}</>;
}
